// Shared helpers for writing games: text formatting, cutting a chart at a
// moment, the engine's indicator reads, and multiple-choice distractors.
import { fmt } from "../desk/deskData";
import { emaSeries, priorVolumeAverage, rsiSeries } from "../chart/indicators";
import { atr, settle, type PracticeTrade } from "../chart/replay/replay";
import type { ChartSpec, GameData, Option, Rng } from "./types";
import type { Candle } from "./types";

export { atr };
export { swings, readStructure, type Swing, type StructureRead } from "../chart/marketStructure";
export { biasCheck, srLevels, pivots, type BiasCheck, type SrLevel } from "../chart/structure";

export const LETTERS = ["A", "B", "C", "D", "E"] as const;

// ── Text ────────────────────────────────────────────────────────────────────

/** A price the way the desk writes it: "64,210.50", "142.37", "0.3412". */
export const price = (n: number): string => fmt(n);

/** "$1,234.50" ("−$12.00" when negative). */
export function usd(n: number, decimals = 2): string {
  const s = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return `${n < 0 ? "−" : ""}$${s}`;
}

/** "2.35%" */
export const pctText = (n: number, decimals = 2): string => `${n < 0 ? "−" : ""}${Math.abs(n).toFixed(decimals)}%`;

/** "+1.5R" / "−1.0R" */
export const rText = (r: number, decimals = 1): string => `${r > 0 ? "+" : r < 0 ? "−" : ""}${Math.abs(r).toFixed(decimals)}R`;

export const roundTo = (n: number, decimals: number): number => {
  const k = 10 ** decimals;
  return Math.round(n * k) / k;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "14 Mar 2026, 13:00 UTC" */
export function dateText(sec: number): string {
  const d = new Date(sec * 1000);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}, ${hh}:${mm} UTC`;
}

/** "13:00" (UTC) */
export function clockText(sec: number): string {
  const d = new Date(sec * 1000);
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

// ── Charts ──────────────────────────────────────────────────────────────────

/** The chart of a game's data: its candles up to "now" and the reveal after. */
export function chartOf(data: GameData, extra: Omit<ChartSpec, "tf" | "candles"> = {}): ChartSpec {
  return { tf: data.tf, candles: data.candles, ahead: data.ahead, ...extra };
}

/**
 * Cut a candle list at the moment `nowSec`: the last `history` candles that
 * had CLOSED by then, and the next `ahead` after them (for the reveal).
 */
export function cutAt(candles: Candle[], nowSec: number, tfSec: number, history: number, ahead = 0): { shown: Candle[]; ahead: Candle[] } {
  let n = 0;
  while (n < candles.length && candles[n]!.time + tfSec <= nowSec) n++;
  return { shown: candles.slice(Math.max(0, n - history), n), ahead: candles.slice(n, n + ahead) };
}

// ── Indicator reads (the engine's formulas) ─────────────────────────────────

/** The EMA at the last candle (engine formula), or null with too few candles. */
export function emaLast(candles: Candle[], period: number): number | null {
  const s = emaSeries(candles.map(c => c.close), period);
  return s[s.length - 1] ?? null;
}

/** RSI 14 at the last candle (engine formula), or null with too few candles. */
export function rsiLast(candles: Candle[], period = 14): number | null {
  const s = rsiSeries(candles.map(c => c.close), period);
  return s[s.length - 1] ?? null;
}

/** The last candle's volume ÷ the 20 candles before it (the trigger rule's "× avg"), or null. */
export function volumeRatio(candles: Candle[], n = 20): number | null {
  const avg = priorVolumeAverage(candles.map(c => c.volume), n);
  const a = avg[avg.length - 1];
  const last = candles[candles.length - 1];
  return a && last ? last.volume / a : null;
}

/** % change from `from` to `to`. */
export const pctChange = (from: number, to: number): number => ((to - from) / from) * 100;

/** R of a price against a plan: + in the trade's favour. */
export function rOf(side: "LONG" | "SHORT", entry: number, stop: number, at: number): number {
  const risk = Math.abs(entry - stop);
  return risk > 0 ? ((side === "SHORT" ? -1 : 1) * (at - entry)) / risk : 0;
}

/**
 * What happened to a plan in the candles after "now", in words — for a
 * question's `outcome` (never its answer). Same settling rule as Replay:
 * a candle that reaches both counts as the stop.
 */
export function planOutcome(side: "LONG" | "SHORT", entry: number, stop: number, target: number, ahead: Candle[]): string {
  if (ahead.length === 0) return "";
  const t: PracticeTrade = { id: "q", side, entry, stop, target, openSec: ahead[0]!.time, exit: null };
  const done = settle(t, ahead);
  if (!done.exit) {
    const last = ahead[ahead.length - 1]!.close;
    return `In the next ${ahead.length} candles price reached neither the stop nor the target (${rText(rOf(side, entry, stop, last))} at the end).`;
  }
  const r = rOf(side, entry, stop, done.exit.price);
  return done.exit.reason === "target"
    ? `Price reached the target first: ${rText(r)}.`
    : `Price hit the stop first: ${rText(r)}.`;
}

// ── Multiple choice ─────────────────────────────────────────────────────────

/**
 * Number options around a right answer: the answer plus `count − 1`
 * distractors spread by `step`, shuffled, all distinct once formatted.
 * Returns the options and the right option's id.
 */
export function numberOptions(
  answer: number,
  rng: Rng,
  format: (n: number) => string,
  opts: { count?: number; step?: number; min?: number } = {},
): { options: Option[]; answer: string } {
  const count = opts.count ?? 4;
  const step = opts.step ?? Math.max(Math.abs(answer) * 0.25, 1e-9);
  const labels = new Set([format(answer)]);
  const values = [answer];
  // Offsets −3…+3 steps around the answer, in random order, keeping the first distinct ones.
  for (const k of rng.shuffle([-3, -2, -1, 1, 2, 3, 4, -4])) {
    if (values.length >= count) break;
    const v = answer + k * step;
    if (opts.min !== undefined && v < opts.min) continue;
    const label = format(v);
    if (labels.has(label)) continue;
    labels.add(label);
    values.push(v);
  }
  const shuffled = rng.shuffle(values);
  const options = shuffled.map((v, i) => ({ id: String(i), label: format(v) }));
  return { options, answer: String(shuffled.indexOf(answer)) };
}
